"use client";

import { useRef } from "react";
import gsap from "gsap";
import { useGSAP } from "@gsap/react";
import { ScrollTrigger } from "gsap/ScrollTrigger";
import { Search, SlidersHorizontal as Sliders, MapPin, Star, Code as Code2, ChartBar as BarChart3, ArrowRight, CircleCheck as CheckCircle2, ShieldCheck, Clock, Sparkles } from 'lucide-react';
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Link } from "@/components/common/Link";
gsap.registerPlugin(useGSAP, ScrollTrigger);
const stepsData = [{
  number: "01",
  title: "Technical & On-Page SEO Audit",
  shortTitle: "Deep Audit",
  description: "I conduct a detailed manual investigation into your website's technical health, indexation issues, architecture, and current keyword visibility.",
  deliverables: ["Technical Debt Analysis", "Content & Keyword Gap Assessment", "Competitor Benchmark Report"],
  icon: Search,
  timeframe: "Week 1 - 2"
}, {
  number: "02",
  title: "Tailored Strategy & Optimization",
  shortTitle: "Optimization",
  description: "I re-architect page structures, rewrite metadata, clean technical code, and refine core landing pages so search engines prioritize your primary services.",
  deliverables: ["On-Page Content Optimization", "Technical Fix Implementation", "Internal Linking Restructure"],
  icon: Sliders,
  timeframe: "Week 3 - 4"
}, {
  number: "03",
  title: "Citations & Local/National Authority",
  shortTitle: "Citations",
  description: "I establish authentic directory profiles, clean up inconsistent NAP data, and earn high-reputation backlinks tailored to your target geographic market.",
  deliverables: ["NAP Citation Cleanup", "Geographic & Niche Directories", "Authority Building Strategy"],
  icon: MapPin,
  timeframe: "Week 5 - 6"
}, {
  number: "04",
  title: "Reviews & Reputation Workflows",
  shortTitle: "Reviews",
  description: "I implement streamlined review collection tactics that build prominent social proof, strengthen local search packs, and elevate conversions.",
  deliverables: ["Google Business Profile Tuning", "Customer Review Acquisition Flow", "Reputation Schema Integration"],
  icon: Star,
  timeframe: "Week 6 - 7"
}, {
  number: "05",
  title: "Custom Schema & Entity Mapping",
  shortTitle: "Schema & AI",
  description: "I code precise JSON-LD structured data so both traditional engines and modern AI search systems (ChatGPT, Perplexity, Gemini) clearly understand your entities.",
  deliverables: ["Custom JSON-LD Implementation", "AI Knowledge Graph Optimization", "Rich Snippets Validation"],
  icon: Code2,
  timeframe: "Week 7 - 8"
}, {
  number: "06",
  title: "Transparent Reporting & Iteration",
  shortTitle: "Reporting",
  description: "No inflated vanity metrics. I deliver custom dashboards and monthly video walkthroughs directly connecting SEO progress with revenue targets.",
  deliverables: ["Custom Looker Studio Dashboard", "Monthly 1-on-1 Video Call", "Quarterly Strategy Refinement"],
  icon: BarChart3,
  timeframe: "Ongoing Monthly"
}];
export default function Process() {
  const container = useRef<HTMLDivElement>(null);
  useGSAP(() => {
    const el = container.current;
    if (!el) return;
    const mm = gsap.matchMedia();
    mm.add("(prefers-reduced-motion: no-preference)", () => {
      gsap.set(".process-header", {
        y: -20
      });
      gsap.to(".process-header", {
        opacity: 1,
        y: 0,
        duration: 0.7,
        ease: "power3.out",
        scrollTrigger: {
          trigger: el,
          start: "top 80%",
          once: true
        }
      });
      gsap.set(".step-card", {
        y: 35
      });
      gsap.to(".step-card", {
        opacity: 1,
        y: 0,
        duration: 0.6,
        stagger: 0.12,
        ease: "power2.out",
        scrollTrigger: {
          trigger: el,
          start: "top 70%",
          once: true
        }
      });
      gsap.set(".cta-banner", {
        scale: 0.96
      });
      gsap.to(".cta-banner", {
        opacity: 1,
        scale: 1,
        duration: 0.6,
        ease: "back.out(1.4)",
        scrollTrigger: {
          trigger: ".cta-banner",
          start: "top 85%",
          once: true
        }
      });
    });
    mm.add("(prefers-reduced-motion: reduce)", () => {
      gsap.set(".process-header, .step-card, .cta-banner", {
        opacity: 1,
        y: 0,
        scale: 1
      });
    });
    const refresh = () => ScrollTrigger.refresh();
    requestAnimationFrame(refresh);
    if (document.readyState === "complete") refresh();else window.addEventListener("load", refresh, {
      once: true
    });
    document.fonts?.ready.then(refresh);
  }, {
    scope: container
  });
  return <section id="audittoreportingprocess" data-nav="light" className="bg-background text-foreground py-20 lg:py-28 relative overflow-hidden">
      <div className="absolute top-0 right-1/4 size-96 bg-accent/10 rounded-full blur-3xl pointer-events-none -z-10" />
      <div className="absolute bottom-10 left-10 size-80 bg-primary/5 rounded-full blur-3xl pointer-events-none -z-10" />

      <div ref={container} className="container mx-auto px-4 sm:px-6 lg:px-8 max-w-7xl">
        <div className="process-header text-center max-w-3xl mx-auto mb-16 lg:mb-24" style={{
        opacity: 0
      }}>
          <Badge variant="outline" className="mb-4 border-primary/30 text-primary bg-primary/5 px-3 py-1">
            <Sparkles className="size-3.5 text-accent mr-1" /> Direct Consultant Execution
          </Badge>

          <h2 className="font-serif text-2xl sm:text-3xl lg:text-4xl font-bold tracking-tight text-foreground mb-4">
            From Initial Audit to Sustainable Revenue Growth
          </h2>

          <p className="text-muted-foreground text-base sm:text-lg leading-relaxed">
            You work directly with me—Yassine Essaadi—at every single stage. Here is the structured, step-by-step SEO methodology I execute to build lasting search visibility for your business.
          </p>
        </div>

        <div className="relative">
          <div className="hidden lg:block absolute top-1/2 left-0 right-0 h-0.5 bg-gradient-to-r from-accent/20 via-primary/30 to-accent/20 -translate-y-12 -z-10" />

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 lg:gap-8">
            {stepsData.map((step, i) => {
            const IconComponent = step.icon;
            return <div key={step.number} data-index={i} className="step-card h-full" style={{
              opacity: 0
            }}>
                  <Card className="h-full border border-border/80 hover:border-primary/50 transition-all duration-300 shadow-sm hover:shadow-md bg-card group relative flex flex-col justify-between">
                    <div>
                      <CardHeader className="pb-3 pt-6 px-6 relative">
                        <div className="flex items-center justify-between mb-4">
                          <div className="size-12 rounded-xl bg-primary text-primary-foreground font-mono font-bold text-lg flex items-center justify-center shadow-sm group-hover:scale-105 transition-transform">
                            {step.number}
                          </div>
                          
                          <div className="flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-accent/15 border border-accent/30 text-xs font-medium text-foreground">
                            <Clock className="size-3 text-accent" />
                            <span>{step.timeframe}</span>
                          </div>
                        </div>

                        <div className="flex items-center gap-2 mb-2">
                          <IconComponent className="size-5 text-primary" />
                          <CardTitle className="font-serif text-xl font-bold text-foreground group-hover:text-primary transition-colors">
                            {step.title}
                          </CardTitle>
                        </div>

                        <p className="text-muted-foreground text-sm leading-relaxed mt-1">
                          {step.description}
                        </p>
                      </CardHeader>

                      <CardContent className="px-6 pb-6 pt-2">
                        <div className="border-t border-border/60 pt-4 mt-2">
                          <p className="text-xs font-semibold uppercase tracking-wider text-muted-foreground mb-3 flex items-center gap-1">
                            <ShieldCheck className="size-3.5 text-primary" /> Key Deliverables
                          </p>
                          <ul className="space-y-2">
                            {step.deliverables.map((item, idx) => <li key={idx} className="flex items-start text-xs text-foreground/90 gap-2">
                                <CheckCircle2 className="size-3.5 text-accent shrink-0 mt-0.5" />
                                <span>{item}</span>
                              </li>)}
                          </ul>
                        </div>
                      </CardContent>
                    </div>

                    <div className="h-1 w-full bg-gradient-to-r from-primary/20 via-accent/40 to-primary/20 rounded-b-xl group-hover:from-primary group-hover:to-accent transition-all duration-300" />
                  </Card>
                </div>;
          })}
          </div>
        </div>

        <div className="cta-banner mt-16 lg:mt-20 rounded-2xl bg-gradient-to-br from-primary via-primary/95 to-primary/90 text-primary-foreground p-8 lg:p-12 shadow-xl relative overflow-hidden text-center lg:text-left flex flex-col lg:flex-row items-center justify-between gap-8 border border-primary/20" style={{
        opacity: 0
      }}>
          <div className="absolute -bottom-10 -right-10 size-60 bg-accent/20 rounded-full blur-2xl pointer-events-none" />

          <div className="max-w-2xl">
            <Badge variant="secondary" className="mb-3 bg-accent text-accent-foreground border-none font-medium px-3 py-1">
              No Agency Middlemen
            </Badge>
            <h3 className="font-serif text-2xl sm:text-3xl font-bold mb-3">
              Ready to Upgrade Your Search Visibility with Direct Expert Advice?
            </h3>
            <p className="text-primary-foreground/90 text-sm sm:text-base leading-relaxed">
              Book a free, non-pushy consultation with me. We will review your current website metrics, outline immediate opportunities, and discuss a tailored strategy.
            </p>
          </div>

          <div className="flex flex-col sm:flex-row gap-4 shrink-0 w-full sm:w-auto">
            <Button asChild size="lg" className="bg-accent text-accent-foreground hover:bg-accent/90 shadow-md font-semibold text-sm px-6">
              <Link to="/ContactPage">
                Book a Free Consultation
                <ArrowRight className="size-4 ml-1" />
              </Link>
            </Button>

            <Button asChild variant="outline" size="lg" className="border-primary-foreground/30 text-primary-foreground hover:bg-primary-foreground/10 font-semibold text-sm px-6">
              <Link to="/CaseStudiesPage">
                Explore Case Studies
              </Link>
            </Button>
          </div>
        </div>
      </div>
    </section>;
}